import { useNavigate } from "react-router-dom";
import { Typography } from "@altimateai/lego";
import { useCreateSession } from "./useWorkflowQueries";
import styles from "./workflow.module.scss";

interface AgentTask {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  icon: string;
  color: string;
  tags: string[];
}

const AGENT_TASKS: AgentTask[] = [
  {
    id: "documenter",
    name: "Documenter",
    subtitle: "Build your team's knowledge base",
    description:
      "Guided sessions to capture institutional knowledge — your role, tools, processes, and tribal wisdom. Creates searchable documentation that helps the whole team.",
    icon: "\u{1F4DD}",
    color: "#6366f1",
    tags: ["knowledge-base", "documentation", "processes"],
  },
  {
    id: "onboarder",
    name: "Onboarder",
    subtitle: "Your AI onboarding buddy",
    description:
      "Interactive companion for new team members. Walks through 30/60/90-day milestones, introduces key systems, and answers questions — like having a senior teammate always available.",
    icon: "\u{1F91D}",
    color: "#10b981",
    tags: ["onboarding", "30-60-90", "mentorship"],
  },
  {
    id: "data-hygienist",
    name: "Data Hygienist",
    subtitle: "Keep your data stack healthy",
    description:
      "Discovers bloated tables, dead tuples, stale indexes, and orphaned records. Recommends and executes cleanup — like the celery_taskmeta table that grew from 2KB to 1GB of dead tuples.",
    icon: "\u{1F9F9}",
    color: "#f59e0b",
    tags: ["vacuum", "table-bloat", "dead-tuples", "cleanup"],
  },
  {
    id: "data-security",
    name: "Data Security",
    subtitle: "Audit and harden access controls",
    description:
      "Identifies overprivileged roles, detects sensitive data exposure, and enforces compliance policies. Continuous audit of your Snowflake RBAC and data access patterns.",
    icon: "\u{1F6E1}\uFE0F",
    color: "#ef4444",
    tags: ["rbac", "audit", "compliance", "pii"],
  },
  {
    id: "data-monitoring",
    name: "Data Monitoring",
    subtitle: "Proactive observability for your data stack",
    description:
      "Watches query performance, warehouse spend, pipeline health, and cost anomalies. Surfaces issues before they impact users and suggests optimizations.",
    icon: "\u{1F4CA}",
    color: "#8b5cf6",
    tags: ["observability", "cost", "performance", "alerts"],
  },
];

const WorkflowTasks = () => {
  const navigate = useNavigate();
  const createMutation = useCreateSession();

  const handleStartTask = (task: AgentTask) => {
    createMutation.mutate(
      {
        title: `${task.name}: New workflow`,
        description: task.description,
        agent_type: task.id,
        priority: "medium",
      },
      {
        onSuccess: data => {
          navigate(`/workflow/sessions/${data.id}`);
        },
      }
    );
  };

  return (
    <div className={styles.tasksPage}>
      <div className={styles.tasksIntro}>
        <Typography variant="body">
          Choose a workflow to start a new session. Each is powered by a specialized AI agent.
        </Typography>
      </div>

      <div className={styles.taskGrid}>
        {AGENT_TASKS.map(task => (
          <div
            key={task.id}
            className={styles.taskCard}
            role="button"
            tabIndex={0}
            onClick={() => handleStartTask(task)}
            onKeyDown={e => { if (e.key === "Enter" || e.key === " ") handleStartTask(task); }}
            style={{ borderTopColor: task.color }}
          >
            <div className={styles.taskCardHeader}>
              <span className={styles.taskCardIcon}>{task.icon}</span>
              <div>
                <h3 className={styles.taskCardTitle}>{task.name}</h3>
                <div className={styles.taskCardSubtitle}>{task.subtitle}</div>
              </div>
            </div>
            <p className={styles.taskCardDescription}>{task.description}</p>
            <div className={styles.taskCardTags}>
              {task.tags.map(tag => (
                <span key={tag} className={styles.taskTag}>
                  {tag}
                </span>
              ))}
            </div>
            <button
              className={styles.taskStartBtn}
              style={{ background: task.color }}
              onClick={e => {
                e.stopPropagation();
                handleStartTask(task);
              }}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? "Starting..." : "Start Workflow"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkflowTasks;
